import { Component } from '@theme/component';

const TOAST_DURATION = 5000;

/**
 * Copies a coupon code to the clipboard and confirms with a dismissible toast.
 *
 * @typedef {object} Refs
 * @property {HTMLTemplateElement} [toastTemplate] - Markup for the shared toast.
 *
 * @extends Component<Refs>
 */
class CouponCodeComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { toastTemplate } = this.refs;
    if (toastTemplate instanceof HTMLTemplateElement) toast.mount(toastTemplate);

    if (this.#isMarqueeDuplicate()) {
      for (const button of this.querySelectorAll('.coupon-code__button')) {
        if (button instanceof HTMLElement) button.tabIndex = -1;
      }
    }
  }

  /**
   * @param {MouseEvent} event - Click on a `.coupon-code__button`.
   */
  async copyCode(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement)) return;

    const { code, copiedMessage, copyFailedMessage } = button.dataset;
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
      toast.show(copiedMessage || code, 'success');
    } catch {
      toast.show(copyFailedMessage || code, 'error');
    }
  }

  /**
   * The marquee clones its items to fill the viewport; only the first copy of
   * each block should be reachable by keyboard.
   */
  #isMarqueeDuplicate() {
    const marquee = this.closest('marquee-component');
    if (!marquee) return false;
    if (this.closest('[aria-hidden="true"]')) return true;

    const first = marquee.querySelector(`coupon-code-component[data-block-id="${CSS.escape(this.dataset.blockId ?? '')}"]`);
    return first !== this;
  }
}

/**
 * Page-wide toast shared by every coupon block. The status region is mounted
 * before the first copy so screen readers announce the first message.
 */
const toast = {
  /** @type {HTMLElement | null} */
  root: null,

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  timer: undefined,

  /**
   * @param {HTMLTemplateElement} template
   */
  mount(template) {
    if (this.root?.isConnected) return;

    const root = template.content.firstElementChild?.cloneNode(true);
    if (!(root instanceof HTMLElement)) return;

    const card = root.querySelector('[data-coupon-toast-card]');
    root.querySelector('[data-coupon-toast-close]')?.addEventListener('click', () => this.hide());
    card?.addEventListener('pointerenter', () => this.pause());
    card?.addEventListener('pointerleave', () => this.resume());
    card?.addEventListener('focusin', () => this.pause());
    card?.addEventListener('focusout', () => this.resume());
    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.hide();
    });

    document.body.append(root);
    this.root = root;
  },

  /**
   * @param {string} message
   * @param {'success' | 'error'} tone
   */
  show(message, tone) {
    const { root } = this;
    if (!root) return;

    const text = root.querySelector('[data-coupon-toast-message]');
    const status = root.querySelector('[data-coupon-toast-status]');
    if (text) text.textContent = message;

    if (status) {
      // Clear first so copying the same code twice is announced again.
      status.textContent = '';
      requestAnimationFrame(() => {
        status.textContent = message;
      });
    }

    root.dataset.tone = tone;
    root.toggleAttribute('data-open', true);
    this.resume();
  },

  hide() {
    clearTimeout(this.timer);
    this.root?.removeAttribute('data-open');
  },

  pause() {
    clearTimeout(this.timer);
  },

  resume() {
    clearTimeout(this.timer);
    if (!this.root?.hasAttribute('data-open')) return;
    this.timer = setTimeout(() => this.hide(), TOAST_DURATION);
  },
};

if (!customElements.get('coupon-code-component')) {
  customElements.define('coupon-code-component', CouponCodeComponent);
}
